import { type Reference, useApolloClient } from "@apollo/client";
import { useCallback, useMemo } from "react";

import WatchlistAddRemoveButtonView from "./WatchlistAddRemoveButtonView";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  OrderByDirection,
  PlayerSearchPlayerFragment,
  SortField,
  usePlayerSearchAddPlayerToWatchlistMutation,
  usePlayerSearchRemovePlayerFromWatchlistMutation,
  WatchlistListMyUserWatchlistsDocument,
  WatchlistListMyUserWatchlistsQuery,
  WatchlistListWatchlistPlayersDocument,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import {
  addOrRemovePlayerInWatchlist,
  changeStatusPlayerInWatchlist,
} from "@/utils/handleWatchlistPlayers";
import { ifWeb } from "@/utils/platform";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * Player accepted by the watchlist add/remove button.
 * @remarks Only identity fields (id, first/last name) are read for the mutation,
 *   so it accepts either a full search-result player or the lightweight
 *   roster/watchlist card node (PlayerCardData) after the SE-5377 roster trim.
 *   `isManual` is optional since this component does not use it.
 */
type WatchlistButtonPlayer =
  | (Omit<PlayerSearchPlayerFragment, "isManual"> & {
      isManual?: boolean;
    })
  | PlayerCardData;

/**
 * Props for the WatchlistAddRemoveButtonContainer component
 */
interface Props {
  watchlist: WatchlistWatchlistMetaFragment;
  player: WatchlistButtonPlayer;
  onAddSuccess?: () => void;
}

/**
 * Container component for watchlist add/remove button with optimistic updates
 * @param root0 - Component props
 * @param root0.player - Player object to add or remove from watchlist
 * @param root0.watchlist - Watchlist to modify with player addition/removal
 * @param root0.onAddSuccess - Callback invoked when player is successfully added to watchlist
 */
const WatchlistAddRemoveButtonContainer = ({
  player,
  watchlist,
  onAddSuccess,
}: Props) => {
  const removePlayerFromWatchListGoal = useRecordUserGoal({
    goalType: GoalType.RemovePlayerFromWatchlist,
    value: player.id,
  });
  const addPlayerToWatchListGoal = useRecordUserGoal({
    goalType: GoalType.AddPlayerToWatchlist,
    value: player.id,
  });
  const toast = useToastNotification();
  const apolloClient = useApolloClient();

  const isPlayerSelected = useMemo(() => {
    // First check if player is in the current page of results
    const foundInCurrentPage = !!watchlist.players?.edges.find(
      p => p.node.id === player.id
    );

    if (foundInCurrentPage) {
      return true;
    }

    // If not found in the current page, try to check the full watchlist in cache
    try {
      const fullWatchlistData = apolloClient.readQuery({
        query: WatchlistListWatchlistPlayersDocument,
        variables: {
          watchlistId: watchlist.id,
          first: 1000, // Large number to get all players
        },
      });

      if (fullWatchlistData?.listWatchlistPlayers) {
        return !!fullWatchlistData.listWatchlistPlayers.edges.find(
          (p: { node: { id: string } }) => p.node.id === player.id
        );
      }
    } catch {
      // If we can't read from cache, fall back to current behavior
    }

    return false;
  }, [watchlist.players?.edges, watchlist.id, player.id, apolloClient]);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const currentUser = useCurrentUser();

  const queriesToRefetch = useMemo(
    () => [
      {
        query: WatchlistListWatchlistPlayersDocument,
        variables: {
          watchlistId: watchlist.id,
          sortBy: SortField.Gpr,
          sortOrder: OrderByDirection.Asc,
        },
      },
      {
        query: WatchlistListWatchlistPlayersDocument,
        variables: {
          watchlistId: watchlist.id,
          sortBy: SortField.Gpr,
          sortOrder: OrderByDirection.Desc,
        },
      },
      {
        query: WatchlistListWatchlistPlayersDocument,
        variables: {
          watchlistId: watchlist.id,
          sortBy: SortField.FitScore,
          sortOrder: OrderByDirection.Asc,
        },
      },
      {
        query: WatchlistListWatchlistPlayersDocument,
        variables: {
          watchlistId: watchlist.id,
          sortBy: SortField.FitScore,
          sortOrder: OrderByDirection.Asc,
        },
      },
    ],
    [watchlist.id]
  );

  const [removePlayer, { loading: removeLoading }] =
    usePlayerSearchRemovePlayerFromWatchlistMutation({
      update: async (cache, { data: dataCache, errors }) => {
        if (!dataCache?.removePlayerFromWatchlist || errors) {
          return;
        }
        // First, update the cache to remove the player from the listWatchlistPlayers
        addOrRemovePlayerInWatchlist(
          cache,
          dataCache.removePlayerFromWatchlist.id,
          player.id,
          "remove"
        );

        // Read the watchlists data and check if player is in any OTHER watchlist
        const watchlistsData: WatchlistListMyUserWatchlistsQuery | null =
          cache.readQuery({
            query: WatchlistListMyUserWatchlistsDocument,
          });

        // Check if player is in any watchlist OTHER than the one we just removed from
        const isInOtherWatchlist =
          watchlistsData?.listMyUserWatchlists?.edges?.some(
            edge =>
              edge.node.watchlist.id !==
                dataCache.removePlayerFromWatchlist.id &&
              edge.node.watchlist.players?.edges?.some(
                playerEdge => playerEdge.node.id === player.id
              )
          ) ?? false;

        // Directly update the isWatchedByCurrentUser field and drop the removed
        // watchlist from Player.watchlists so the SE-5024 indicator updates
        // without a refetch. The mutation does not return Player.watchlists, so
        // Apollo cannot normalize this automatically — Option B (targeted
        // cache.modify) is preferred over bloating the mutation selection set.
        const removedWatchlistId = dataCache.removePlayerFromWatchlist.id;
        cache.modify({
          id: cache.identify({ __typename: "Player", id: player.id }),
          fields: {
            isWatchedByCurrentUser() {
              return isInOtherWatchlist;
            },
            watchlists(existing: readonly Reference[] = [], { readField }) {
              return existing.filter(
                ref => readField("id", ref) !== removedWatchlistId
              );
            },
          },
        });
      },
    });
  const [addPlayer, { loading: addLoading }] =
    usePlayerSearchAddPlayerToWatchlistMutation({
      update: (cache, { data: dataCache, errors }) => {
        if (!dataCache?.addPlayerToWatchlist || errors) {
          return;
        }
        // Update the cache to reflect the status of the new player
        changeStatusPlayerInWatchlist(cache, player.id);

        // Update the cache to add the new player to the listWatchlistPlayers
        addOrRemovePlayerInWatchlist(
          cache,
          dataCache.addPlayerToWatchlist.id,
          player.id,
          "add"
        );

        // Append the newly-joined watchlist summary to Player.watchlists so the
        // SE-5024 indicator updates without a refetch. The mutation does not
        // return Player.watchlists, so Apollo cannot normalize this
        // automatically — Option B (targeted cache.modify) is preferred over
        // bloating the mutation selection set (which is already heavy).
        const addedWatchlistId = dataCache.addPlayerToWatchlist.id;
        const addedWatchlistName = dataCache.addPlayerToWatchlist.name;
        // Explicitly set the Entity generic on `cache.modify` so Apollo's
        // single-field inference doesn't narrow the modifier signature to
        // `Modifier<Reference | readonly Reference[]>` (which has a
        // `DeepPartial<readonly Reference[]>` return that's hard to satisfy
        // without an `as any` cast). Matching the multi-field shape used by
        // `removePlayer` above keeps both modifiers structurally identical.
        cache.modify<{ watchlists: readonly Reference[] }>({
          id: cache.identify({ __typename: "Player", id: player.id }),
          fields: {
            watchlists(existing = [], { readField, toReference }) {
              // Apollo's modifier value may be a singleton Reference for some
              // entity shapes; for `watchlists` it's always an array in
              // practice, but we normalize defensively to keep TS happy.
              const existingList: readonly Reference[] = Array.isArray(existing)
                ? (existing as readonly Reference[])
                : [];
              const alreadyPresent = existingList.some(
                ref => readField("id", ref) === addedWatchlistId
              );
              if (alreadyPresent) return [...existingList];
              const newRef = toReference(
                {
                  __typename: "WatchlistSummary",
                  id: addedWatchlistId,
                  name: addedWatchlistName,
                },
                true
              );
              return newRef ? [...existingList, newRef] : [...existingList];
            },
          },
        });
      },
    });

  const handleChange = useCallback(() => {
    // Belt-and-suspenders against rapid double-tap (SE-5235 AC2): the View
    // disables on `loading`, but the disabled prop only takes effect on the
    // next render. Short-circuit here too so a second tap fired in the same
    // tick cannot dispatch a duplicate add/remove mutation.
    if (addLoading || removeLoading) {
      return;
    }
    if (isPlayerSelected) {
      removePlayer({
        variables: {
          input: { watchlistId: watchlist.id, playerId: player.id },
        },
        // No optimisticResponse: this button only holds the lightweight meta
        // watchlist (id-only players), but the removePlayerFromWatchlist
        // mutation selects the full WatchlistWatchlist player detail. Writing an
        // incomplete optimistic result flooded the console with Apollo
        // "Missing field" (error 13) warnings (SE-5058). The update() callback
        // below already reconciles membership + isWatchedByCurrentUser in the
        // cache once the mutation resolves, so optimistic UI is preserved
        // without the malformed write.
        onCompleted: () => {
          removePlayerFromWatchListGoal();
          toast.show({
            description: "Player removed from watchlist",
            action: "success",
          });

          AnalyticsTracker.user.logPlayerWatchlistRemoved({
            user_id: currentUser?.id || "",
            organization_id: currentUser?.organizationId || "",
            phone_number: currentUser?.phoneNumber || "",
            watchlist_id: watchlist.id,
            watchlist_name: watchlist.name,
            player_id: player.id,
            player_name: player?.name ?? "",
            action: "removed",
            platform: ifWeb("web", "mobile"),
            timestamp: new Date().toISOString(),
          });
        },
      });
    } else {
      addPlayer({
        variables: {
          input: { watchlistId: watchlist.id, playerId: player.id },
        },
        refetchQueries: queriesToRefetch,
        // No optimisticResponse — see the removePlayer call above. The
        // addPlayerToWatchlist mutation selects the full WatchlistWatchlist, so
        // an incomplete optimistic write from the meta watchlist produced an
        // Apollo "Missing field" (error 13) flood. update() reconciles the
        // cache on resolve instead.
        onCompleted: () => {
          addPlayerToWatchListGoal();
          onAddSuccess?.();
          toast.show({
            description: "Player added to list",
            action: "success",
          });

          AnalyticsTracker.user.logPlayerWatchlistAdded({
            user_id: currentUser?.id || "",
            organization_id: currentUser?.organizationId || "",
            phone_number: currentUser?.phoneNumber || "",
            watchlist_id: watchlist.id,
            watchlist_name: watchlist.name,
            player_id: player.id,
            player_name: player?.name ?? "",
            action: "added",
            platform: ifWeb("web", "mobile"),
            timestamp: new Date().toISOString(),
          });
        },
      });
    }
  }, [
    addLoading,
    addPlayer,
    addPlayerToWatchListGoal,
    isPlayerSelected,
    onAddSuccess,
    player,
    queriesToRefetch,
    removeLoading,
    removePlayer,
    removePlayerFromWatchListGoal,
    toast,
    watchlist,
    currentUser,
  ]);

  return (
    <WatchlistAddRemoveButtonView
      isPlayerSelected={isPlayerSelected}
      onChange={handleChange}
      watchlist={watchlist}
      // Disable the control while EITHER mutation is in flight so a rapid
      // double-tap cannot fire a duplicate add/remove (SE-5235 AC2).
      loading={removeLoading || addLoading}
      isSmallScreen={isSmallScreen}
      isDark={isDark}
    />
  );
};
export default WatchlistAddRemoveButtonContainer;
